import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, Container, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useMediaQuery, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import React from 'react';
import { useInView } from 'react-intersection-observer';

export default function ComparisonSection() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [ref] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    const comparisonData = [
        {
            feature: '7/24 Yanıt Süresi',
            helpigo: '1 dakika',
            competitor: '15-30 dakika',
            isBetter: true
        },
        {
            feature: 'Geçmiş Sorulardan Öğrenme',
            helpigo: 'Var',
            competitor: 'Yok',
            isBetter: true
        },
        {
            feature: 'Yapay Zeka Teknolojisi',
            helpigo: 'GPT Temelli',
            competitor: 'Basit NLP',
            isBetter: true
        },
        {
            feature: 'Özelleştirilebilir Yanıtlar',
            helpigo: 'Var',
            competitor: 'Sınırlı',
            isBetter: true
        },
        {
            feature: 'Mağaza Entegrasyonu',
            helpigo: 'Kolay',
            competitor: 'Karmaşık',
            isBetter: true
        },
        {
            feature: 'Analitik Raporlama',
            helpigo: 'Detaylı',
            competitor: 'Temel',
            isBetter: true
        },
        {
            feature: 'Fiyatlandırma',
            helpigo: 'Uygun',
            competitor: 'Pahalı',
            isBetter: true
        }
    ];

    return (
        <Box
            id="comparison"
            sx={{
                py: { xs: 6, md: 10 },
                background: 'white',
                borderTop: `1px solid ${theme.palette.grey[100]}`,
            }}
        >
            <Container maxWidth="lg">
                <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        viewport={{ once: true }}
                    >
                        <Typography
                            variant="h2"
                            component="h2"
                            sx={{
                                fontSize: { xs: '1.75rem', sm: '2rem', md: '2.75rem' },
                                fontWeight: 700,
                                mb: 2,
                                color: theme.palette.text.primary,
                                letterSpacing: '-0.02em',
                            }}
                        >
                            Rakiplerle Karşılaştırma
                        </Typography>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        viewport={{ once: true }}
                    >
                        <Typography
                            variant="body1"
                            sx={{
                                fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                                color: theme.palette.text.secondary,
                                maxWidth: '800px',
                                mx: 'auto',
                                mb: 4,
                                px: { xs: 2, sm: 0 }
                            }}
                        >
                            Helpigo, rakiplerine göre daha hızlı, daha doğru ve daha özelleştirilebilir bir çözüm sunar.
                        </Typography>
                    </motion.div>
                </Box>

                <motion.div
                    ref={ref}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    viewport={{ once: true }}
                >
                    <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{
                            borderRadius: '12px',
                            overflow: 'hidden',
                            border: '1px solid rgba(0,0,0,0.08)',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                        }}
                    >
                        <Table sx={{ minWidth: isMobile ? 300 : 650 }}>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
                                    <TableCell
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: { xs: '0.85rem', md: '1rem' },
                                            color: theme.palette.secondary.main,
                                            py: { xs: 2, md: 3 },
                                            px: { xs: 1, sm: 2, md: 3 }
                                        }}
                                    >
                                        Özellik
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: { xs: '0.85rem', md: '1rem' },
                                            color: theme.palette.primary.main,
                                            py: { xs: 2, md: 3 },
                                            px: { xs: 1, sm: 2, md: 3 }
                                        }}
                                    >
                                        Helpigo
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: { xs: '0.85rem', md: '1rem' },
                                            color: theme.palette.secondary.main,
                                            py: { xs: 2, md: 3 },
                                            px: { xs: 1, sm: 2, md: 3 }
                                        }}
                                    >
                                        Rakipler
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {comparisonData.map((row, index) => (
                                    <TableRow
                                        key={index}
                                        sx={{
                                            '&:nth-of-type(odd)': { backgroundColor: 'rgba(0,0,0,0.01)' },
                                            '&:last-child td, &:last-child th': { border: 0 },
                                            transition: 'background-color 0.3s',
                                            '&:hover': {
                                                backgroundColor: 'rgba(37,99,235,0.04)',
                                            },
                                        }}
                                    >
                                        <TableCell
                                            component="th"
                                            scope="row"
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: { xs: '0.8rem', md: '0.9rem' },
                                                py: { xs: 1.5, md: 2 },
                                                px: { xs: 1, sm: 2, md: 3 }
                                            }}
                                        >
                                            {row.feature}
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                color: theme.palette.primary.main,
                                                fontWeight: 600,
                                                fontSize: { xs: '0.8rem', md: '0.9rem' },
                                                py: { xs: 1.5, md: 2 },
                                                px: { xs: 1, sm: 2, md: 3 }
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                {row.helpigo}
                                                <CheckCircleIcon
                                                    sx={{
                                                        ml: 1,
                                                        color: theme.palette.success.main,
                                                        fontSize: { xs: '1rem', md: '1.25rem' }
                                                    }}
                                                />
                                            </Box>
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                color: row.isBetter ? theme.palette.text.secondary : theme.palette.success.main,
                                                fontWeight: 500,
                                                fontSize: { xs: '0.8rem', md: '0.9rem' },
                                                py: { xs: 1.5, md: 2 },
                                                px: { xs: 1, sm: 2, md: 3 }
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                {row.competitor}
                                                {row.isBetter && (
                                                    <CancelIcon
                                                        sx={{
                                                            ml: 1,
                                                            color: theme.palette.error.main,
                                                            fontSize: { xs: '1rem', md: '1.25rem' }
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </motion.div>
            </Container>
        </Box>
    );
} 